import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { RecordPageShell } from '~/components/layout';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardSection,
  Disclosure,
  FormActions,
  PanelTable,
  type PanelTableColumn,
} from '~/components/ui';
import { Checkbox, RadioCardGroup, ToggleField } from '~/components/form';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import { routes } from '~/app/routes';
import { plural } from '~/lib/format';
import { useGetCustomersQuery } from '~/modules/query/customers';
import type { DecouplingAgent } from '~/modules/query/decoupling';
import { useDecoupleMutation, useGetWizardDataQuery } from '~/modules/query/decoupling';
import { queryErrorMessage } from '~/modules/query/base';

// The disconnect-social page for one customer: loads its agents/feeds, collects the
// listing-conflict choices and the options, then confirms in place of the form before submitting.
// The confirm is a step of the page, never an overlay.

/** The agent fields the wizard shows (the rest ride along to the API untouched). */
type WizardAgent = DecouplingAgent & {
  firstName?: string;
  lastName?: string;
  bayAccountId?: string | number | null;
  team?: string;
};

type ListingAgent = { id: string; name?: string; nListings?: number; isSelected?: boolean };

interface DisconnectSocialWizardProps {
  customerMoniker: string;
  customerName: string;
  reachbackYears?: number;
}

const AGENT_COLUMNS: PanelTableColumn<WizardAgent>[] = [
  { header: 'First', render: (agent) => agent.firstName },
  { header: 'Last', render: (agent) => agent.lastName },
  {
    header: 'Public ID',
    render: (agent) => <span className="font-mono text-xs">{agent.publicId}</span>,
  },
  { header: 'BAY account', render: (agent) => agent.bayAccountId },
  { header: 'Team', render: (agent) => agent.team },
];

const DisconnectSocialWizard = ({
  customerMoniker,
  customerName,
  reachbackYears = 1,
}: DisconnectSocialWizardProps) => {
  const navigate = useNavigate();
  const [mapListings, setMapListings] = useState(true);
  const [pushPasswords, setPushPasswords] = useState(true);
  const [listingSelections, setListingSelections] = useState<Record<string, string>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const moniker = customerMoniker.trim();
  const wizardQuery = useGetWizardDataQuery(
    { customerMoniker: moniker, reachbackYears },
    { skip: !moniker },
  );
  const wizardData = wizardQuery.data;
  const [decouple, decoupleState] = useDecoupleMutation();
  const agentList = (wizardData?.agents ?? []) as WizardAgent[];
  const guard = useUnsavedGuard(
    !decoupleState.isSuccess &&
      (confirmOpen || !mapListings || !pushPasswords || Object.keys(listingSelections).length > 0),
    'The disconnect has not been sent. If you leave this page, your choices will be lost.',
  );

  useEffect(() => {
    if (!wizardData) return;
    if (!wizardData.agents?.length || !wizardData.mlss?.length) {
      toast.warn('This customer has no agents or no feeds to disconnect');
    }
  }, [wizardData]);

  const conflictedAgents = useMemo(() => {
    if (!mapListings || !wizardData?.agents) return [];
    return (wizardData.agents as WizardAgent[])
      .map((agent) => {
        const conflicts = Object.entries(agent.listingAgentsByMls ?? {}).filter(
          ([, la]) => la.length > 1,
        );
        return conflicts.length ? { agent, conflicts } : null;
      })
      .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
  }, [wizardData, mapListings]);

  const unresolvedCount = useMemo(
    () =>
      conflictedAgents.reduce(
        (count, { agent, conflicts }) =>
          count +
          conflicts.filter(([mlsMoniker]) => !listingSelections[`${agent.publicId}:${mlsMoniker}`])
            .length,
        0,
      ),
    [conflictedAgents, listingSelections],
  );

  const submit = async () => {
    if (!wizardData) return;
    const agents = agentList.map((agent) => {
      if (!agent.listingAgentsByMls) return agent;
      const listingAgentsByMls = Object.fromEntries(
        Object.entries(agent.listingAgentsByMls).map(([mlsMoniker, listingAgents]) => {
          const selectedId = listingSelections[`${agent.publicId}:${mlsMoniker}`];
          return [
            mlsMoniker,
            listingAgents.map((la) => ({
              ...la,
              isSelected: listingAgents.length === 1 ? true : la.id === selectedId,
            })),
          ];
        }),
      );
      return { ...agent, listingAgentsByMls };
    });

    try {
      await decouple({
        customerMoniker: moniker,
        agents,
        mlss: wizardData.mlss,
        mapListings,
        pushPasswords,
      }).unwrap();
      toast.success(`Customer '${moniker}' disconnected from Office and Social disabled`);
      guard.release();
      navigate(routes.customers);
    } catch (err) {
      console.error(err);
      toast.error(queryErrorMessage(err) ?? 'Social could not be disconnected. Try again.');
    }
  };

  return (
    <RecordPageShell
      title={`Disconnect Social for ${customerName}`}
      description="Disconnects this customer from Office and disables Social."
      breadcrumbs={[
        { label: 'Customers', href: routes.customers },
        { label: `Disconnect Social for ${customerName}` },
      ]}
      loading={wizardQuery.isLoading}
      error={
        wizardQuery.error
          ? { what: 'the customer data', message: queryErrorMessage(wizardQuery.error) }
          : null
      }
      onRetry={() => wizardQuery.refetch()}
      guard={guard}
      testId="disconnect"
    >
      {wizardData && (
        <Card>
          {confirmOpen ? (
            <>
              <CardBody className="space-y-4">
                <div data-testid="disconnect-confirm" className="space-y-4">
                  <p className="text-sm text-gray-800 dark:text-white/90">
                    This disconnects <strong>{moniker}</strong> from Office and zeroes out BAY
                    account ids for {plural(agentList.length, 'agent')}. Social is disabled for the
                    customer. This cannot be undone from this screen.
                  </p>
                  {unresolvedCount > 0 && (
                    <p className="text-sm text-warning-600 dark:text-warning-400">
                      Resolve the {unresolvedCount} listing conflict
                      {unresolvedCount === 1 ? '' : 's'} before continuing.
                    </p>
                  )}
                  <Checkbox
                    id="disconnect-agree"
                    testId="disconnect-agree-checkbox"
                    checked={agreed}
                    onChange={setAgreed}
                    text="I agree"
                  />
                </div>
              </CardBody>
              <CardFooter>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmOpen(false)}
                  disabled={decoupleState.isLoading}
                  testId="disconnect-confirm-back"
                >
                  Back
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={!agreed || unresolvedCount > 0}
                  loading={decoupleState.isLoading}
                  onClick={submit}
                  testId="disconnect-confirm-submit"
                >
                  Disconnect Social
                </Button>
              </CardFooter>
            </>
          ) : (
            <>
              <CardBody className="space-y-6">
                <CardSection title="Options">
                  <div className="space-y-1">
                    <ToggleField
                      id="disconnect-map-listings"
                      testId="disconnect-map-listings-toggle"
                      inline
                      checked={mapListings}
                      onChange={setMapListings}
                      text={`Map listings to Social feeds (${wizardData.mlss?.map((mls) => mls.description).join(', ') || 'no feeds'})`}
                    />
                    <ToggleField
                      id="disconnect-push-passwords"
                      testId="disconnect-push-passwords-toggle"
                      inline
                      checked={pushPasswords}
                      onChange={setPushPasswords}
                      text="Push agent passwords to Social"
                    />
                  </div>
                </CardSection>

                <Disclosure title={`Agents to disconnect (${agentList.length})`}>
                  <div className="p-4">
                    <PanelTable<WizardAgent>
                      ariaLabel="Agents to disconnect"
                      testId="disconnect-agents-table"
                      maxHeight="18rem"
                      columns={AGENT_COLUMNS}
                      data={agentList}
                      getRowId={(agent) => agent.publicId ?? ''}
                      emptyMessage="No agents to disconnect."
                      emptyDescription="Every agent under this customer is already disconnected."
                    />
                  </div>
                </Disclosure>

                {conflictedAgents.length > 0 && (
                  <CardSection title={`Resolve listing agents (${unresolvedCount} left)`}>
                    <Alert variant="warning">
                      <p className="text-sm text-gray-800 dark:text-white/90">
                        These agents have listings in the same feed under more than one
                        listing-agent record. For each feed, pick the record Social should keep when
                        the listings are re-pointed to the standalone feed. The number in
                        parentheses is how many listings that record has.
                      </p>
                    </Alert>
                    <div className="space-y-4">
                      {conflictedAgents.map(({ agent, conflicts }) => (
                        <div key={agent.publicId} className="space-y-3">
                          <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                            {agent.firstName} {agent.lastName} ({agent.publicId})
                          </p>
                          {conflicts.map(([mlsMoniker, listingAgents]) => (
                            <div key={mlsMoniker} className="space-y-2">
                              <p className="font-mono text-xs text-gray-500 dark:text-gray-400">
                                {mlsMoniker}
                              </p>
                              <RadioCardGroup
                                name={`${agent.publicId}:${mlsMoniker}`}
                                ariaLabel={`Listing agent record for ${agent.firstName} ${agent.lastName} in ${mlsMoniker}`}
                                value={listingSelections[`${agent.publicId}:${mlsMoniker}`] ?? ''}
                                onChange={(id) =>
                                  setListingSelections((prev) => ({
                                    ...prev,
                                    [`${agent.publicId}:${mlsMoniker}`]: id,
                                  }))
                                }
                                options={(listingAgents as ListingAgent[]).map((listingAgent) => ({
                                  value: listingAgent.id,
                                  label: `${listingAgent.name ?? listingAgent.id} (${listingAgent.nListings == null ? 'listings unknown' : plural(listingAgent.nListings, 'listing')})`,
                                }))}
                              />
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </CardSection>
                )}
              </CardBody>
              <CardFooter>
                <FormActions
                  discardHref={routes.customers}
                  discardLabel="Cancel"
                  saveLabel="Continue"
                  onSave={() => setConfirmOpen(true)}
                  saveTestId="disconnect-continue-button"
                />
              </CardFooter>
            </>
          )}
        </Card>
      )}
    </RecordPageShell>
  );
};

/** `/customers/:id/disconnect-social`: names the customer, then runs the wizard on the page. */
export const DisconnectSocialPage = () => {
  const { id = '' } = useParams<{ id: string }>();
  const customers = useGetCustomersQuery();
  const rows = customers.data as
    | { companyid: string | number; company_name?: string }[]
    | undefined;
  const customer = rows?.find((row) => String(row.companyid) === id);

  if (rows && !customer) {
    return (
      <RecordPageShell
        title="Disconnect Social"
        breadcrumbs={[
          { label: 'Customers', href: routes.customers },
          { label: 'Disconnect Social' },
        ]}
        notFound
        notFoundTitle="Customer not found"
        backTo={routes.customers}
        backLabel="Back to customers"
        testId="disconnect"
      />
    );
  }
  return (
    <DisconnectSocialWizard
      key={id}
      customerMoniker={id}
      customerName={customer?.company_name ?? id}
    />
  );
};
